import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { itineraryApi } from '../services/api';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Map, FileText, Clock, Star, Compass, DollarSign, Camera,
  Mail, Plane, Hotel, Globe, Calculator, Luggage, Scale,
  UserPlus, Shield,
} from 'lucide-react';
import { RevenueSection, RevenueDialog } from '../components/admin/RevenueSection';
import { UsersSection } from '../components/admin/UsersSection';
import { MarketingSection } from '../components/admin/MarketingSection';
import { RecentActivity } from '../components/admin/RecentActivity';
import { TrendCharts } from '../components/admin/TrendCharts';

const AMBER = '#D97706';
const GREEN = '#4C735B';

const StatCard = ({ icon: Icon, label, value, color = AMBER, testId }) => (
  <div className="bg-white border border-stone-200 rounded-2xl p-5 transition-all duration-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-amber-300/40" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</p>
        <p className="text-2xl font-bold text-stone-900">{value}</p>
      </div>
    </div>
  </div>
);

const ServiceCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-3 transition-all duration-200 hover:shadow-sm" data-testid={testId}>
    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50">
      <Icon className="w-4 h-4 text-emerald-700" />
    </div>
    <div className="flex-1">
      <p className="text-xs text-stone-500">{label}</p>
      <p className="text-lg font-bold text-stone-800">{value}</p>
    </div>
  </div>
);

const AddAdminDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error('Email and password are required');
    setSubmitting(true);
    try {
      await itineraryApi.addAdminUser(form);
      toast.success('Admin user created successfully');
      setForm({ email: '', password: '', first_name: '', last_name: '' });
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create admin user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-stone-900">
            <Shield className="w-5 h-5 text-amber-600" /> Add Admin User
          </DialogTitle>
          <DialogDescription>Create a new administrator account with full dashboard access.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">First Name</label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" data-testid="add-admin-first-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600 mb-1 block">Last Name</label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" data-testid="add-admin-last-name" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Email *</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin@example.com" required data-testid="add-admin-email" />
          </div>
          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Password *</label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" required minLength={6} data-testid="add-admin-password" />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-amber-600 text-white hover:bg-amber-700 rounded-full px-6" data-testid="add-admin-submit-btn">
              {submitting ? 'Creating...' : 'Create Admin'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminDashboardPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState(null);
  const [trends, setTrends] = useState({ registrations: [], trips: [] });
  const [users, setUsers] = useState([]);
  const [usersMeta, setUsersMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [recentActivity, setRecentActivity] = useState({ recent_trips: [], recent_contacts: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [revenue, setRevenue] = useState({ entries: [], summary: { total_revenue: 0, entry_count: 0, by_source: {}, monthly_trend: [] } });
  const [revenueDialogOpen, setRevenueDialogOpen] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState(null);
  const [revenueForm, setRevenueForm] = useState({ amount: '', source: '', description: '', date: '' });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, trendsRes, activityRes, revenueRes] = await Promise.all([
        itineraryApi.getAdminStats(),
        itineraryApi.getAdminTrends(30),
        itineraryApi.getAdminRecentActivity(),
        itineraryApi.getRevenue(),
      ]);
      setStats(statsRes.data.stats);
      setServices(statsRes.data.services);
      setTrends({ registrations: trendsRes.data.registrations, trips: trendsRes.data.trips });
      setRecentActivity(activityRes.data);
      if (revenueRes.data.success) setRevenue({ entries: revenueRes.data.entries, summary: revenueRes.data.summary });
    } catch (err) {
      if (err.response?.status === 403) navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    try {
      const res = await itineraryApi.getAdminUsers({ page, limit: 10, search });
      setUsers(res.data.users);
      setUsersMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages });
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!isAdmin) { navigate('/dashboard'); return; }
    fetchData();
    fetchUsers(1, '');
  }, [isAdmin, navigate, fetchData, fetchUsers]);

  useEffect(() => {
    const t = setTimeout(() => fetchUsers(1, searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery, fetchUsers]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchUsers(newPage, searchQuery);
  };

  const chartData = trends.registrations.map((r, i) => ({
    date: r.date?.slice(5) || '',
    registrations: r.count,
    trips: trends.trips[i]?.count || 0,
  }));

  const fetchRevenueOnly = async () => {
    try {
      const res = await itineraryApi.getRevenue();
      if (res.data.success) setRevenue({ entries: res.data.entries, summary: res.data.summary });
    } catch { /* silent */ }
  };

  const openRevenueDialog = (entry = null) => {
    if (entry) {
      setEditingRevenue(entry);
      setRevenueForm({ amount: String(entry.amount), source: entry.source, description: entry.description || '', date: entry.date });
    } else {
      setEditingRevenue(null);
      setRevenueForm({ amount: '', source: '', description: '', date: new Date().toISOString().slice(0, 10) });
    }
    setRevenueDialogOpen(true);
  };

  const handleRevenueSave = async (e) => {
    e.preventDefault();
    if (!revenueForm.amount || !revenueForm.source || !revenueForm.date) return toast.error('Amount, source, and date are required');
    try {
      if (editingRevenue) {
        await itineraryApi.updateRevenue(editingRevenue.id, { ...revenueForm, amount: parseFloat(revenueForm.amount) });
        toast.success('Revenue entry updated');
      } else {
        await itineraryApi.addRevenue({ ...revenueForm, amount: parseFloat(revenueForm.amount) });
        toast.success('Revenue entry added');
      }
      setRevenueDialogOpen(false);
      fetchRevenueOnly();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleRevenueDelete = async (id) => {
    try {
      await itineraryApi.deleteRevenue(id);
      toast.success('Revenue entry deleted');
      fetchRevenueOnly();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-[#FDFDF9] pt-20 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full" />
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#FDFDF9] dark:bg-[#161b22] pt-20 pb-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-testid="admin-dashboard-title">Admin Dashboard</h1>
              <p className="text-sm text-stone-500 mt-1">Welcome back, {user?.first_name || 'Admin'}. Here's your platform overview.</p>
            </div>
            <Button onClick={() => setAddDialogOpen(true)} className="bg-amber-600 text-white hover:bg-amber-700 rounded-full px-5 gap-2" data-testid="add-admin-btn">
              <UserPlus className="w-4 h-4" /> Add Admin User
            </Button>
          </div>

          {/* Overview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <StatCard icon={Users} label="Users" value={stats?.total_users || 0} color={AMBER} testId="total-users-metric" />
            <StatCard icon={Map} label="Itineraries" value={stats?.total_itineraries || 0} color={GREEN} testId="total-itineraries-metric" />
            <StatCard icon={FileText} label="Blogs" value={stats?.total_blogs || 0} color="#0284C7" testId="total-blogs-metric" />
            <StatCard icon={Clock} label="Stories" value={stats?.total_stories || 0} color="#7C3AED" testId="total-stories-metric" />
            <StatCard icon={Star} label="Reviews" value={stats?.total_reviews || 0} color="#DC2626" testId="total-reviews-metric" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <StatCard icon={Compass} label="Templates" value={stats?.total_templates || 0} color={GREEN} testId="total-templates-metric" />
            <StatCard icon={DollarSign} label="Expenses" value={stats?.total_expenses || 0} color={AMBER} testId="total-expenses-metric" />
            <StatCard icon={Users} label="Companions" value={stats?.total_companions || 0} color="#0284C7" testId="total-companions-metric" />
            <StatCard icon={Camera} label="Photos" value={stats?.total_journal_photos || 0} color="#7C3AED" testId="total-photos-metric" />
            <StatCard icon={Mail} label="Contacts" value={stats?.total_contacts || 0} color="#DC2626" testId="total-contacts-metric" />
          </div>

          {/* Services Usage */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-stone-900 mb-4" data-testid="services-section-title">Services Usage</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <ServiceCard icon={Plane} label="Flight Searches" value={services?.flight_searches || 0} testId="flight-searches-metric" />
              <ServiceCard icon={Hotel} label="Hotel Searches" value={services?.hotel_searches || 0} testId="hotel-searches-metric" />
              <ServiceCard icon={Globe} label="Visa Checks" value={services?.visa_checks || 0} testId="visa-checks-metric" />
              <ServiceCard icon={Calculator} label="Budget Calcs" value={services?.budget_calculations || 0} testId="budget-calcs-metric" />
              <ServiceCard icon={Luggage} label="Packing Lists" value={services?.packing_lists || 0} testId="packing-lists-metric" />
              <ServiceCard icon={Scale} label="Compare" value={services?.compare_destinations || 0} testId="compare-metric" />
            </div>
          </div>

          <RevenueSection revenue={revenue} onAddEntry={() => openRevenueDialog()} onEditEntry={openRevenueDialog} onDeleteEntry={handleRevenueDelete} />
          <TrendCharts chartData={chartData} />
          <MarketingSection />
          <RecentActivity recentActivity={recentActivity} />
          <UsersSection users={users} usersMeta={usersMeta} searchQuery={searchQuery} setSearchQuery={setSearchQuery} currentPage={currentPage} setCurrentPage={setCurrentPage} onPageChange={handlePageChange} />
        </div>
      </div>

      <AddAdminDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} onCreated={() => fetchUsers(currentPage, searchQuery)} />
      <RevenueDialog open={revenueDialogOpen} onOpenChange={setRevenueDialogOpen} editingRevenue={editingRevenue} revenueForm={revenueForm} setRevenueForm={setRevenueForm} onSave={handleRevenueSave} />
    </>
  );
}
